import { __awaiter } from "tslib";
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { SearchForRetail } from '@/client/api';
import { getToken, getTokenForced } from '@/auth/auth-access';
import { getTenantName } from '@/utils';
import { logError } from '@/utils/logger';
import { CC_API_TAG_LIST } from './constants';
/**
 * Inner base query with automatic Auth0 token and tenant header injection.
 */
const ccBaseQuery = fetchBaseQuery({
    baseUrl: SearchForRetail.API_URL,
    prepareHeaders: (headers) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const token = yield getToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
        }
        catch (error) {
            // Token retrieval failed - request will proceed without auth header
            // This allows the API to return a proper 401 response
            logError('Failed to retrieve auth token', error);
        }
        // Add customer ID header
        const customerId = getTenantName();
        if (customerId) {
            headers.set('X-Groupby-Customer-Id', customerId);
        }
        return headers;
    }),
});
/**
 * Wraps ccBaseQuery with automatic re-authentication on 401 responses.
 *
 * When the backend returns 401 (Unauthorized), this wrapper:
 * 1. Attempts to silently refresh the Auth0 token via getToken()
 * 2. If the refresh succeeds, retries the original request once
 * 3. If the refresh still fails, returns the 401 error to the caller
 *
 * This prevents the user from being shown raw 401 errors during an active
 * session when the access token has simply expired and can be renewed.
 * If the refresh token itself is expired, the 401 propagates and is handled
 * by the Auth0LoginLoaderProvider (which shows a permission notification or
 * triggers a re-login).
 */
const baseQueryWithReauth = (args, api, extraOptions) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const result = yield ccBaseQuery(args, api, extraOptions);
    if (((_a = result.error) === null || _a === void 0 ? void 0 : _a.status) === 401) {
        try {
            // getTokenForced() bypasses the SDK cache and performs a refresh token exchange.
            // The fresh token is written back to the Auth0 SDK cache, so the subsequent
            // ccBaseQuery retry — which calls getToken() via prepareHeaders — will pick it up.
            const freshToken = yield getTokenForced();
            if (freshToken) {
                const retryResult = yield ccBaseQuery(args, api, extraOptions);
                return retryResult;
            }
        }
        catch (error) {
            logError('Token refresh failed after 401', error);
        }
        // Refresh failed — return the original 401 error.
        // Auth0LoginLoaderProvider and the inactivity monitor handle the UX
        // (re-login prompt, permission notification, etc.)
    }
    return result;
});
/**
 * API slice for RTK Query.
 *
 * This provides the foundation for all API endpoints that interact with the CC API backend.
 * Specific API slices should use `injectEndpoints` to add their endpoints.
 */
export const ccApi = createApi({
    reducerPath: 'ccApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: CC_API_TAG_LIST,
    endpoints: () => ({}),
});
